import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { AdminDashboardCompany, AdminDashboardSortKey, SortDirection } from '../../../models/adminDashboard';
import { getCompanyOrderTotal } from '../../../services/admin/dashboardService';

export interface CompanyTableProps {
  /** Rows to render, already sorted and paginated by the parent. */
  companies: AdminDashboardCompany[];
  sortKey: AdminDashboardSortKey;
  sortDirection: SortDirection;
  onSort: (key: AdminDashboardSortKey) => void;
}

function sortIndicator(active: boolean, direction: SortDirection): string {
  if (!active) {
    return '';
  }
  return direction === 'asc' ? ' ▲' : ' ▼';
}

export function CompanyTable({ companies, sortKey, sortDirection, onSort }: CompanyTableProps): React.ReactElement {
  const navigate = useNavigate();

  return (
    <div className="bo-card">
      <div className="bo-admin-table-head">
        <h2 className="bo-admin-section-title">Companies</h2>
        <p className="bo-muted">Click a company to open operational detail.</p>
      </div>
      <div className="bo-admin-table-wrap">
        <table className="bo-admin-table">
          <thead>
            <tr>
              <th>
                <button type="button" className="bo-link-button" onClick={() => onSort('companyName')}>
                  Company{sortIndicator(sortKey === 'companyName', sortDirection)}
                </button>
              </th>
              <th>
                <button type="button" className="bo-link-button" onClick={() => onSort('merchantCount')}>
                  Merchants{sortIndicator(sortKey === 'merchantCount', sortDirection)}
                </button>
              </th>
              <th>
                <button type="button" className="bo-link-button" onClick={() => onSort('status')}>
                  Status{sortIndicator(sortKey === 'status', sortDirection)}
                </button>
              </th>
              <th>
                <button type="button" className="bo-link-button" onClick={() => onSort('activeOrders')}>
                  Orders{sortIndicator(sortKey === 'activeOrders', sortDirection)}
                </button>
              </th>
              <th>Order mix</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company) => {
              const isActive = company.status.toLowerCase() === 'active';
              const orderTotal = getCompanyOrderTotal(company);
              return (
                <tr key={company.companyId} onClick={() => navigate(`/admin/companies/${encodeURIComponent(company.companyId)}`)}>
                  <td>
                    <div className="bo-admin-company-cell">
                      <strong>{company.companyName}</strong>
                      <span className="bo-muted">{company.companyId}</span>
                    </div>
                  </td>
                  <td>{company.merchantCount}</td>
                  <td>
                    <span className={`bo-status-pill ${isActive ? 'bo-status-active' : 'bo-status-inactive'}`}>
                      {company.status}
                    </span>
                  </td>
                  <td>{orderTotal}</td>
                  <td>
                    <div className="bo-admin-order-mix">
                      {Object.entries(company.orderCountsByStatus).map(([status, count]) => (
                        <span key={`${company.companyId}-${status}`} className="bo-admin-order-chip">
                          {status}: {count}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
